export const services = [
  {
    slug: "work-permits",
    title: "Work Permits",
    shortDescription: "Unlock international career opportunities with expert work permit guidance.",
    description: "Navigate the complex world of international work permits with NexFLY's expert guidance. We help professionals and skilled workers obtain work authorization in top destination countries, ensuring a smooth transition to your new career abroad.",
    icon: "Briefcase",
    image: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80",
    eligibility: [
      "Valid passport with minimum 6 months validity",
      "Relevant educational qualifications",
      "Work experience in the desired field",
      "Language proficiency requirements",
      "Clean criminal background",
      "Medical fitness certificate"
    ],
    process: [
      { step: 1, title: "Initial Assessment", description: "We evaluate your profile, qualifications, and career goals to determine the best work permit pathway." },
      { step: 2, title: "Document Preparation", description: "Our team helps you gather and prepare all necessary documents for your application." },
      { step: 3, title: "Application Filing", description: "We file your work permit application with precision and attention to detail." },
      { step: 4, title: "Follow-up & Updates", description: "We track your application status and provide regular updates until approval." }
    ],
    features: [
      "Comprehensive eligibility assessment",
      "Document preparation and review",
      "Application filing and tracking",
      "Interview preparation",
      "Post-approval settlement guidance",
      "Employer liaison services"
    ],
    faqs: [
      { question: "How long does a work permit application take?", answer: "Processing times vary by country, typically ranging from 4 weeks to 6 months. Our experts will provide a realistic timeline based on your specific case." },
      { question: "Can my family accompany me on a work permit?", answer: "Most countries allow dependent visas for spouses and children. We can help process family applications alongside your work permit." },
      { question: "What if my work permit application is denied?", answer: "We analyze the reasons for denial and advise on the best course of action, whether it's reapplication or an alternative pathway." }
    ]
  },
  {
    slug: "study-visa",
    title: "Study Visa",
    shortDescription: "Pursue world-class education abroad with our comprehensive study visa assistance.",
    description: "Transform your academic dreams into reality with NexFLY's study visa services. From university selection to visa approval, we guide students through every step of their international education journey.",
    icon: "GraduationCap",
    image: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&q=80",
    eligibility: [
      "Acceptance letter from a recognized institution",
      "Proof of financial capability",
      "Academic transcripts and certificates",
      "Language proficiency test scores (IELTS/TOEFL)",
      "Valid passport",
      "Statement of Purpose"
    ],
    process: [
      { step: 1, title: "University Selection", description: "We help you choose the right university and program based on your academic goals and budget." },
      { step: 2, title: "Application Support", description: "Complete assistance with university applications, including SOP and recommendation letters." },
      { step: 3, title: "Visa Filing", description: "Expert guidance through the visa application process with document preparation." },
      { step: 4, title: "Pre-Departure Briefing", description: "Comprehensive orientation about your destination country, accommodation, and settlement." }
    ],
    features: [
      "University and course selection guidance",
      "Application and SOP review",
      "Scholarship assistance",
      "Visa interview preparation",
      "Pre-departure orientation",
      "Post-landing support"
    ],
    faqs: [
      { question: "Which countries are popular for studying abroad?", answer: "Canada, UK, Australia, USA, Germany, and New Zealand are among the top choices. We help you select the best fit based on your goals and budget." },
      { question: "Do I need IELTS for a study visa?", answer: "Most English-speaking countries require IELTS or equivalent. Some universities offer conditional admission with language courses." },
      { question: "Can I work while studying abroad?", answer: "Most student visas allow part-time work during term and full-time during breaks. Rules vary by country." }
    ]
  },
  {
    slug: "permanent-residency",
    title: "Permanent Residency",
    shortDescription: "Secure your future with expert PR application guidance and support.",
    description: "Build a permanent life in your dream country with NexFLY's PR services. Our experienced consultants help you navigate the complex permanent residency application process with confidence and clarity.",
    icon: "Home",
    image: "https://images.unsplash.com/photo-1542037104857-ffbb0b9155fb?w=800&q=80",
    eligibility: [
      "Meeting minimum points requirement (where applicable)",
      "Work experience in eligible occupations",
      "Language proficiency (IELTS/PTE/CELPIP)",
      "Educational Credential Assessment (ECA)",
      "Clean background check",
      "Medical examination clearance"
    ],
    process: [
      { step: 1, title: "Profile Evaluation", description: "Comprehensive assessment of your eligibility using the latest immigration point systems." },
      { step: 2, title: "Express Entry / EOI Filing", description: "Strategic filing of your expression of interest to maximize invitation chances." },
      { step: 3, title: "Application Submission", description: "Complete PR application with all supporting documents carefully prepared." },
      { step: 4, title: "Landing & Settlement", description: "Post-approval guidance for your smooth transition to permanent life abroad." }
    ],
    features: [
      "CRS score optimization",
      "Provincial Nominee Program guidance",
      "Document authentication support",
      "Background check assistance",
      "Medical exam coordination",
      "Landing preparation services"
    ],
    faqs: [
      { question: "What is the minimum CRS score needed for Canada PR?", answer: "CRS scores vary with each draw. Our team monitors trends and helps you optimize your score for the best chances." },
      { question: "How long does the PR process take?", answer: "Express Entry typically takes 6-12 months. PNP and other pathways may vary. We provide realistic timelines." },
      { question: "Can I include my family in my PR application?", answer: "Yes, you can include your spouse/partner and dependent children in your PR application." }
    ]
  },
  {
    slug: "tourist-visa",
    title: "Tourist Visa",
    shortDescription: "Explore the world with hassle-free tourist visa processing services.",
    description: "Planning an international trip shouldn't be stressful — and with NexFLY, it won't be. Our tourist visa services cover destinations across Europe, North America, Asia, and beyond. We handle the entire application process for you, from compiling the required documents and preparing financial statements to filling out application forms with precision. Whether you're planning a family vacation, a honeymoon, a business visit, a religious pilgrimage, or a cultural exploration trip, our experts know exactly what each embassy requires and how to present your case for the best chances of approval. We also assist with travel insurance, flight bookings, and itinerary planning to make your trip truly hassle-free.",
    icon: "Plane",
    image: "https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=80",
    eligibility: [
      "Valid passport with adequate validity",
      "Proof of sufficient funds",
      "Travel itinerary and hotel bookings",
      "Travel insurance",
      "Employment or business proof",
      "Previous travel history (recommended)"
    ],
    process: [
      { step: 1, title: "Travel Planning", description: "We help you plan your trip and identify the visa requirements for your destination." },
      { step: 2, title: "Document Compilation", description: "Complete checklist and assistance with gathering required documents." },
      { step: 3, title: "Application Filing", description: "We prepare and submit your visa application with precision." },
      { step: 4, title: "Visa Collection", description: "Track your application and collect your approved visa." }
    ],
    features: [
      "Multiple destination expertise",
      "Fast-track processing options",
      "Document checklist and preparation",
      "Travel insurance guidance",
      "Itinerary planning assistance",
      "Group visa processing"
    ],
    faqs: [
      { question: "How far in advance should I apply for a tourist visa?", answer: "We recommend applying 4-8 weeks before your planned travel date. Some countries may require longer processing times." },
      { question: "Do I need travel insurance for a tourist visa?", answer: "Many countries require travel insurance as part of the visa application. We can guide you on the right coverage." },
      { question: "Can I extend my tourist visa?", answer: "Extension policies vary by country. Some allow extensions while others require you to exit and re-apply." }
    ]
  },
  {
    slug: "investor-visa",
    title: "Investor Visa",
    shortDescription: "Invest in your future with strategic investor visa pathways worldwide.",
    description: "Take your entrepreneurial ambitions global with NexFLY's comprehensive investor visa services. Whether you're looking to expand an existing business or start a new venture overseas, we guide you through every aspect of the investor immigration process. Our team evaluates your investment profile, identifies the most suitable programs across multiple countries, assists in preparing compelling business plans, and ensures all financial documentation meets the stringent requirements of immigration authorities. We've helped business owners and investors secure their path to residency through strategic investment pathways in Europe, North America, and beyond.",
    icon: "TrendingUp",
    image: "https://images.unsplash.com/photo-1454496406107-dc34337da8d6?w=800&q=80",
    eligibility: [
      "Minimum investment capital as per country requirements",
      "Legitimate source of funds documentation",
      "Business plan or investment proposal",
      "Relevant business experience",
      "Clean criminal and financial record",
      "Health insurance coverage"
    ],
    process: [
      { step: 1, title: "Investment Assessment", description: "Evaluate your investment capacity and identify the best investor visa program." },
      { step: 2, title: "Business Plan Development", description: "Craft a compelling business plan that meets immigration requirements." },
      { step: 3, title: "Application & Investment", description: "File your application with complete investment documentation." },
      { step: 4, title: "Approval & Setup", description: "Post-approval business setup support and settlement guidance." }
    ],
    features: [
      "Multiple program comparison",
      "Business plan assistance",
      "Financial documentation support",
      "Legal compliance guidance",
      "Post-landing business setup",
      "Family inclusion support"
    ],
    faqs: [
      { question: "What is the minimum investment required?", answer: "Investment requirements vary significantly by country and program, ranging from $100,000 to over $1 million. We'll find the best match for your budget." },
      { question: "Can I bring my family on an investor visa?", answer: "Most investor visa programs include provisions for spouse and dependent children." },
      { question: "Do I need to actively manage the business?", answer: "Requirements vary by program. Some require active management while others allow passive investment." }
    ]
  },
  {
    slug: "ielts-coaching",
    title: "IELTS Coaching",
    shortDescription: "Achieve your target IELTS score with expert coaching and proven strategies.",
    description: "Achieve your dream IELTS band score with NexFLY's intensive and personalized coaching program. Our certified trainers bring years of experience and a deep understanding of the IELTS exam pattern to every session. We offer structured courses covering all four modules — Listening, Reading, Writing, and Speaking — with tailored strategies for each. From diagnostic assessments that identify your weak areas to full-length mock tests with detailed feedback, our program is designed to build your confidence and sharpen your skills. Whether you need IELTS for university admissions, work visa applications, or permanent residency requirements, we'll help you get there.",
    icon: "BookOpen",
    image: "https://images.unsplash.com/20/cambridge.JPG?w=800&q=80",
    eligibility: [
      "Basic English communication ability",
      "Commitment to regular practice sessions",
      "Access to study materials (provided)",
      "Minimum 4-8 weeks preparation time recommended",
      "Familiarity with test format (we'll teach this)",
      "Goal band score identified"
    ],
    process: [
      { step: 1, title: "Diagnostic Test", description: "Assess your current English level and identify areas for improvement." },
      { step: 2, title: "Customized Study Plan", description: "Tailored coaching plan based on your target score and timeline." },
      { step: 3, title: "Intensive Training", description: "Regular sessions covering Listening, Reading, Writing, and Speaking modules." },
      { step: 4, title: "Mock Tests & Feedback", description: "Full-length practice tests with detailed feedback to track progress." }
    ],
    features: [
      "Small batch sizes for personalized attention",
      "Experienced and certified trainers",
      "Comprehensive study material",
      "Regular mock tests and assessments",
      "Speaking practice with feedback",
      "Flexible batch timings"
    ],
    faqs: [
      { question: "How long does IELTS preparation take?", answer: "Typically 4-8 weeks depending on your current level and target score. We offer both intensive and regular programs." },
      { question: "What band score can I expect?", answer: "Results depend on your starting level and dedication. Our students typically improve by 1-2 bands with our coaching." },
      { question: "Do you offer online coaching?", answer: "Yes, we offer both in-person and online coaching options to suit your schedule and preferences." }
    ]
  },
  {
    slug: "german-language",
    title: "German Language Program",
    shortDescription: "Learn German from scratch to fluency with our structured language program.",
    description: "Unlock a world of opportunities in Germany and across German-speaking Europe with NexFLY's structured German language coaching program. Germany is one of the top destinations for skilled professionals, nurses, engineers, and IT specialists — and learning the language is your first step to getting there. Our program covers all levels from A1 (complete beginner) through B2 (upper intermediate), following the Common European Framework of Reference (CEFR). Classes are led by trained instructors who focus on practical communication, grammar, vocabulary, and cultural understanding. We also prepare you for the official Goethe Institut certification exam, which is widely recognized for German visa and job applications.",
    icon: "Languages",
    image: "https://images.unsplash.com/photo-1535483102974-fa1e64d0ca86?w=800&q=80",
    eligibility: [
      "No prior German knowledge required for beginners",
      "Commitment to regular class attendance",
      "Access to learning materials (provided)",
      "Minimum age of 16 years",
      "Willingness to practice outside class",
      "Clear goal (study/work/immigration)"
    ],
    process: [
      { step: 1, title: "Level Assessment", description: "Determine your current German proficiency and set learning goals." },
      { step: 2, title: "Course Enrollment", description: "Join the appropriate level class with our structured curriculum." },
      { step: 3, title: "Interactive Learning", description: "Engaging classes with focus on all four language skills." },
      { step: 4, title: "Certification", description: "Prepare for and take the official Goethe Institut examination." }
    ],
    features: [
      "A1 to B2 level courses available",
      "Native-speaking trained instructors",
      "Goethe Institut exam preparation",
      "Cultural integration sessions",
      "Conversational practice groups",
      "Flexible weekend and weekday batches"
    ],
    faqs: [
      { question: "How long does it take to reach B2 level?", answer: "Reaching B2 from scratch typically takes 8-12 months with regular study. Intensive programs can accelerate this timeline." },
      { question: "Is this program recognized for German visa applications?", answer: "Yes, our program prepares you for Goethe Institut certification, which is widely recognized for German visa applications." },
      { question: "Can I join without any German knowledge?", answer: "Absolutely! Our A1 course is designed for complete beginners with no prior knowledge of German." }
    ]
  },
  {
    slug: "consultation",
    title: "Consultation Services",
    shortDescription: "Get personalized immigration advice from certified consultants.",
    description: "Not sure which immigration pathway is the right fit for you? NexFLY's one-on-one consultation services are designed to give you clarity, confidence, and a concrete action plan. Our certified immigration consultants take the time to thoroughly assess your educational background, work experience, language skills, financial situation, and long-term goals. Based on this detailed profile evaluation, we recommend the most suitable visa categories and countries, outline realistic timelines, and help you understand the costs involved. Whether you're a fresh graduate exploring study abroad options, a working professional seeking career growth overseas, a family planning to relocate permanently, or an individual dealing with a previous visa refusal, our consultations are tailored to your unique situation.",
    icon: "MessageCircle",
    image: "https://images.unsplash.com/photo-1553697388-94e804e2f0f6?w=800&q=80",
    eligibility: [
      "Anyone considering immigration or study abroad",
      "Professionals seeking career opportunities abroad",
      "Students planning international education",
      "Families looking to relocate permanently",
      "Investors exploring global opportunities",
      "Individuals with previous visa refusals"
    ],
    process: [
      { step: 1, title: "Book Appointment", description: "Schedule a consultation at your convenience, in-person or online." },
      { step: 2, title: "Profile Assessment", description: "Our consultant thoroughly evaluates your profile and goals." },
      { step: 3, title: "Strategy Discussion", description: "Receive a detailed roadmap with recommended immigration pathways." },
      { step: 4, title: "Action Plan", description: "Get a clear action plan with timelines and next steps." }
    ],
    features: [
      "One-on-one expert consultations",
      "Comprehensive profile evaluation",
      "Multiple pathway analysis",
      "Refusal case assessment",
      "Online and in-person options",
      "Follow-up support included"
    ],
    faqs: [
      { question: "How long is a consultation session?", answer: "A typical consultation lasts 45-60 minutes, covering all aspects of your immigration query in detail." },
      { question: "Can I book an online consultation?", answer: "Yes, we offer consultations via video call for clients who prefer remote meetings." },
      { question: "Is the consultation fee adjustable against service charges?", answer: "Yes, if you proceed with our services, the consultation fee is typically adjusted against the total service charges." }
    ]
  }
];

export const teamMembers = [
  {
    name: "Nanda Gopal Ramesh",
    role: "Chairman",
    description: "Visionary leader driving NexFLY's mission to make international opportunities accessible to everyone.",
    initials: "NR"
  },
  {
    name: "Prathosh R",
    role: "Managing Director",
    description: "Strategic leader overseeing operations and ensuring exceptional service delivery for all clients.",
    initials: "PR"
  }
];

export const testimonials = [
  {
    name: "Karthik Murugan",
    role: "Mechanical Engineer, Czech Republic",
    content: "NexFLY helped me secure my work permit for Czech Republic seamlessly. From document preparation to the final approval, their guidance was exceptional. I'm now settled and thriving in my career abroad!",
    rating: 5
  },
  {
    name: "Surya Prakash",
    role: "IT Professional, Poland",
    content: "Getting a work permit for Poland felt overwhelming until I approached NexFLY. Their team handled everything professionally and kept me informed at every stage. Highly recommend their services!",
    rating: 5
  },
  {
    name: "Deepika Venkatesh",
    role: "Student, University of Manchester, UK",
    content: "NexFLY made my dream of studying in the UK come true. From selecting the right university to visa approval, they were with me every step. I'm now pursuing my Masters at the University of Manchester!",
    rating: 5
  }
];

export const blogPosts = [
  {
    id: 1,
    slug: "canada-express-entry-2025-guide",
    title: "Complete Guide to Canada Express Entry 2025",
    excerpt: "Everything you need to know about the Canadian Express Entry system, including recent changes and tips to maximize your CRS score.",
    content: "The Canadian Express Entry system continues to be one of the most popular pathways for skilled workers seeking permanent residency in Canada. In 2025, several updates have been introduced that affect how candidates are ranked and invited.\n\nThe Comprehensive Ranking System (CRS) remains the backbone of Express Entry, evaluating candidates based on age, education, work experience, and language proficiency. Recent draws have shown varying cut-off scores, making it crucial to optimize every aspect of your profile.\n\nKey changes in 2025 include expanded category-based draws targeting specific occupations in healthcare, STEM, trades, and agriculture. Provincial Nominee Programs (PNPs) continue to offer additional pathways with 600 bonus CRS points.\n\nTo maximize your chances, consider improving your language scores, obtaining additional education credentials, and gaining Canadian work experience if possible. Our consultants at NexFLY can help you develop a strategic plan tailored to your profile.",
    category: "Immigration",
    author: "Rajesh Sharma",
    date: "2025-07-15",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1500835556837-99ac94a94552?w=600&q=80"
  },
  {
    id: 2,
    slug: "top-universities-australia-2025",
    title: "Top 10 Universities in Australia for International Students",
    excerpt: "Discover the best Australian universities offering world-class education, scholarships, and post-study work opportunities.",
    content: "Australia has emerged as a premier destination for international students, offering world-class education, vibrant multicultural communities, and excellent post-study work opportunities.\n\nThe Group of Eight (Go8) universities continue to dominate global rankings, with the University of Melbourne, University of Sydney, and Australian National University leading the pack. These institutions offer cutting-edge research facilities and strong industry connections.\n\nFor international students, Australia offers the Temporary Graduate Visa (subclass 485) allowing 2-4 years of post-study work rights. Scholarships from both universities and the Australian government can significantly reduce costs.\n\nPopular fields include Engineering, IT, Healthcare, Business, and Data Science. Many programs include industry placements, giving students practical experience before graduation.",
    category: "Study Abroad",
    author: "Priya Patel",
    date: "2025-07-01",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=600&q=80"
  },
  {
    id: 3,
    slug: "ielts-preparation-tips",
    title: "10 Proven IELTS Preparation Tips for Band 7+",
    excerpt: "Expert strategies and practice techniques to help you achieve your target IELTS band score in your first attempt.",
    content: "Achieving a high IELTS band score is often the key that unlocks doors to international education and immigration opportunities. Here are proven strategies from our expert coaches.\n\n1. Understand the test format thoroughly before you begin preparing.\n2. Focus on your weakest module first - most students struggle with Writing.\n3. Practice under timed conditions to build exam stamina.\n4. For Listening, practice with various English accents.\n5. For Reading, develop skimming and scanning techniques.\n6. For Writing Task 2, learn to structure essays with clear arguments.\n7. For Speaking, practice with a partner or record yourself.\n8. Read English newspapers and academic journals daily.\n9. Use official Cambridge IELTS practice tests.\n10. Take at least 3-4 full mock tests before the actual exam.\n\nRemember, consistency in practice is more important than the number of hours you put in.",
    category: "IELTS",
    author: "Sarah Chen",
    date: "2025-06-20",
    readTime: "5 min read",
    image: "https://images.unsplash.com/20/cambridge.JPG?w=600&q=80"
  },
  {
    id: 4,
    slug: "germany-work-visa-opportunities",
    title: "Growing Work Visa Opportunities in Germany 2025",
    excerpt: "Germany's new immigration policies are opening doors for skilled workers. Learn about the latest opportunities and requirements.",
    content: "Germany's Skilled Immigration Act has transformed the country into one of the most attractive destinations for international professionals. The 2025 updates have further simplified the process.\n\nThe new Opportunity Card (Chancenkarte) allows skilled workers to enter Germany for up to one year to seek employment. This points-based system evaluates candidates on qualifications, language skills, work experience, and age.\n\nKey sectors with high demand include IT, engineering, healthcare, skilled trades, and education. German language proficiency (minimum B1) significantly improves your chances, though many IT positions accept English-only speakers.\n\nNexFLY's German Language Program is specifically designed to prepare candidates for both the language requirements and cultural integration needed for a successful career in Germany.",
    category: "Work Permits",
    author: "Amit Kumar",
    date: "2025-06-10",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1535483102974-fa1e64d0ca86?w=600&q=80"
  },
  {
    id: 5,
    slug: "investor-visa-comparison",
    title: "Investor Visa Comparison: USA vs UK vs Canada",
    excerpt: "A comprehensive comparison of investor visa programs across three popular destinations to help you make the right choice.",
    content: "Choosing the right investor visa program requires careful analysis of investment requirements, processing times, benefits, and long-term residency options.\n\nThe USA's EB-5 program requires a minimum investment of $800,000 in a Targeted Employment Area, creating at least 10 full-time jobs. Processing can take 18-24 months.\n\nThe UK's Innovator Founder visa requires an endorsed innovative business idea with appropriate funding. It offers a pathway to settlement after 3 years.\n\nCanada's Start-Up Visa Program targets entrepreneurs with innovative business ideas supported by designated organizations. It offers direct permanent residency upon approval.\n\nEach program has unique advantages. The best choice depends on your investment capacity, business goals, and preferred lifestyle. Our consultants can help you evaluate all options.",
    category: "Investor Visa",
    author: "Rajesh Sharma",
    date: "2025-05-25",
    readTime: "9 min read",
    image: "https://images.unsplash.com/photo-1454496406107-dc34337da8d6?w=600&q=80"
  },
  {
    id: 6,
    slug: "tourist-visa-schengen-guide",
    title: "Schengen Tourist Visa: A Step-by-Step Application Guide",
    excerpt: "Navigate the Schengen visa application process with ease using our comprehensive step-by-step guide.",
    content: "Planning a European vacation? The Schengen visa gives you access to 27 European countries with a single visa. Here's your complete guide to a successful application.\n\nStep 1: Determine which country to apply to - generally the country where you'll spend the most time.\nStep 2: Gather required documents including passport, photos, travel insurance, flight bookings, hotel reservations, bank statements, and employment letter.\nStep 3: Schedule an appointment at the consulate or visa application center.\nStep 4: Attend the appointment with all original documents.\nStep 5: Pay the visa fee and provide biometrics.\nStep 6: Wait for processing (typically 15-45 days).\n\nPro tips: Apply 4-6 weeks before travel. Show strong ties to your home country. Ensure bank statements show consistent balance. Travel insurance must cover minimum EUR 30,000.",
    category: "Tourist Visa",
    author: "Priya Patel",
    date: "2025-05-15",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=600&q=80"
  }
];

export const stats = [
  { value: "3+", label: "Years of Experience" },
  { value: "500+", label: "Successful Cases" },
  { value: "15+", label: "Countries Covered" },
  { value: "98%", label: "Success Rate" }
];

export const countries = [
  "Europe", "Australia", "Canada", "Singapore", "United Kingdom"
];

export const contactInfo = {
  phone: "+91 82483 23674",
  email: "Support@nexfly.co.in",
  address: "Royal Complex, Shop 8, 597/2, Venugopal Layout, Pappanaickenpalayam, Coimbatore - 641037",
  hours: "Mon to Fri: 10:00 AM to 6:00 PM",
  social: {
    facebook: "https://facebook.com/nexfly",
    instagram: "https://instagram.com/nexfly",
    linkedin: "https://linkedin.com/company/nexfly",
    twitter: "https://twitter.com/nexfly"
  }
};
